<template>
    <div class="overview-container">
        <alert :show-alert="showAlert" :message="alertMessage" :type="alertType" />
        <div v-if="getCurrentSMSCampaign" class="info-container">
            <v-card class="table-wrapper">
                <campaign-table :rows="campaignTableRows">
                    <template v-slot:file_used>
                        <v-btn
                            color="primary"
                            class="my-2 white--text"
                            @click="downloadFile"
                        >
                            Download
                            <v-icon right dark> mdi-file-download-outline </v-icon>
                        </v-btn>
                    </template>
                    <template v-slot:status>
                        <v-icon
                            v-if="getCurrentSMSCampaign.completedAt"
                            color="success"
                            class="inline-icon"
                        >
                            mdi-check-circle-outline
                        </v-icon>
                        <v-icon v-else color="orange" class="inline-icon">
                            mdi-clock-outline
                        </v-icon>
                    </template>
                </campaign-table>
                <delete-dialog
                    v-if="hasDeletePrivilege"
                    :disable-delete="hasDeletePrivilege"
                    class="delete-dialog"
                    @delete-event="deleteSMSCampaign()"
                />
            </v-card>
            <v-card
                v-if="statusCounts.length > 0"
                class="charts-container"
            >
                <div>
                    <radial-bar chart-title="Sms Messages Sent" :progress="progress" />
                </div>
                <div class="chart-row">
                    <donut-chart chart-title="Sms Messages Status" :status-counts="statusCounts" />
                    <simple-table :status-counts="statusCounts" />
                </div>

            </v-card>
        </div>
        <v-card class="table-container">
            <div class="card-title">
                <div>{{ cardTitle }}</div>
                <div>
                    <v-btn :disabled="disableExportButton" class="export-btn" @click="exportCSV"> Export </v-btn>
                </div>
            </div>
            <array-view
                :items="smsMessages"
                :headers="headers"
                :total-pages="totalPages"
                :page="page"
                :size="size"
                :total-items="totalItems"
                :has-search-field="true"
                :update-filter="updateFilter"
                :is-fetching="isFetchingSms"
                should-rows-expand
                @paginationChange="paginationChange"
            />
        </v-card>
        <div id="export" />
    </div>
</template>

<script>
    import dateTimeUtils from '@/utils/date-time-utils'
    import smsCampaignService from '@/services/sms-campaign-service'
    import RadialBar from '@/components/RadialBar.vue'
    import DonutChart from '@/components/DonutChart.vue'
    import SimpleTable from '@/components/SimpleTable.vue'
    import ArrayView from '@/components/ArrayView.vue'
    import CampaignTable from '@/components/CampaignTable.vue'
    import tableUtils from '@/utils/tableUtils'
    import { hasPrivilege } from '@/utils/userUtils'
    import { PRIVILEGES } from '@/utils/consts'
    import DeleteDialog from '@/components/DeleteDialog.vue'
    import Alert from '@/components/Alert.vue'
    export default {
        components: {
            CampaignTable,
            RadialBar,
            DonutChart,
            SimpleTable,
            ArrayView,
            DeleteDialog,
            Alert
        },
        props: {
            campaign: {
                type: Object,
                required: false,
                default: null
            }
        },
        data() {
            return {
                showAlert: false,
                disableExportButton: false,
                alertMessage: '',
                alertType: 'error',
                submitLoading: false,
                statusCounts: [],
                progress: 0,
                headers: [
                    {
                        text: '#',
                        value: 'index'
                    },
                    {
                        text: 'Id',
                        value: 'id'
                    },
                    { text: 'Mobile Number', value: 'recipient' },
                    { text: 'Text', value: 'smsText' },
                    { text: 'SMS Parts', value: 'parts' },
                    { text: 'Date Sent', value: 'sentDate' },
                    { text: 'Status', value: 'status' },
                    { text: 'Date of Status', value: 'statusDate' },
                    { text: '', value: 'data-table-expand' }
                ],
                totalPages: 0,
                size: 5,
                page: 1,
                smsMessages: [],
                cardTitle: 'SMS List',
                csvString: '',
                totalItems: 0,
                smsFilter: '',
                isFetchingSms: false
            }
        },
        computed: {
            hasDeletePrivilege() {
                return hasPrivilege(
                    PRIVILEGES.SMS_CAMPAIGN_DELETE_PRIVILEGE,
                    this.$store.getters.getCurrentUserPrivileges
                )
            },
            campaignTableRows() {
                return [
                    {
                        header: 'Id :',
                        value: this.getCurrentSMSCampaign.id
                    },
                    {
                        header: 'Campaign :',
                        value: this.getCurrentSMSCampaign.alias
                    },
                    {
                        header: 'Originator :',
                        value: this.getCurrentSMSCampaign.originator
                    },
                    {
                        header: 'SMS Template :',
                        value: this.getCurrentSMSCampaign.textTemplate
                    },
                    {
                        header: 'Alias :',
                        value: this.getCurrentSMSCampaign.alias
                    },
                    {
                        header: 'Created At :',
                        value: this.getHumanizedDateTime(
                            this.getCurrentSMSCampaign.createdAt
                        )
                    },
                    {
                        header: 'File Used :',
                        type: 'slot',
                        slot: 'file_used'
                    },
                    {
                        header: 'Scheduled At :',
                        value: this.getHumanizedDateTime(
                            this.getCurrentSMSCampaign.scheduledAt
                        )
                    },
                    {
                        header: 'Started At :',
                        value: this.getCurrentSMSCampaign.startedAt
                            ? this.getHumanizedDateTime(this.getCurrentSMSCampaign.startedAt)
                            : 'N/A'
                    },
                    {
                        header: 'Completed At :',
                        value: this.getCurrentSMSCampaign.completedAt
                            ? this.getHumanizedDateTime(this.getCurrentSMSCampaign.completedAt)
                            : 'N/A'
                    },
                    {
                        header: 'Numbers :',
                        value: this.totalItems ?? 0
                    },
                    {
                        header: 'Parameters :',
                        value: this.getCurrentSMSCampaign.params
                    }
                ]
            },
            getCurrentSMSCampaign() {
                return this.campaign
            },
            filter() {
                return {
                    filter: { q: this.smsFilter },
                    range: [this.page - 1, this.size]
                }
            }
        },
        watch: {
            getCurrentSMSCampaign() {
                this.initDetails()
            },
            showAlert: {
                handler(newval) {
                    if (newval) {
                        setTimeout(() => { this.showAlert = false }, 3000)
                    }
                }
            }
        },
        created() {
            this.initDetails()
        },
        methods: {
            initDetails() {
                if (this.getCurrentSMSCampaign.id) {
                    smsCampaignService
                        .getSMSCampaignProgress(this.getCurrentSMSCampaign.id)
                        .then((resp) => {
                            this.progress = resp.data.percentage
                        })
                    smsCampaignService
                        .getSMSCampaignStatusMessages(this.getCurrentSMSCampaign.id)
                        .then((resp) => {
                            this.statusCounts = resp.data.content
                        })
                        .catch((error) => {
                            this.setAlert(error.response.data.message, 'error')
                        })
                    this.fetchAllSMSMessages()
                }
            },
            getHumanizedDateTime(dateTime) {
                return dateTimeUtils.getHumanizedDateTime(dateTime)
            },
            submitCampaign() {
                this.submitLoading = true
                return smsCampaignService
                    .submitSMSCampaign(this.getCurrentSMSCampaign.id)
                    .then(() => {
                        this.submitLoading = false
                        this.setAlert('Campaign Created', 'success')
                        setTimeout(() => {
                            this.$router.push(`/sms/${this.getCurrentSMSCampaign.id}`)
                        }, 3000)
                    })
                    .catch((error) => {
                        this.setAlert(error.response.data.message, 'error')
                    })
            },
            emitPrevStep() {
                this.$emit('prev-step')
            },
            reset() {
                this.$emit('reset')
            },
            cancel() {
                this.$emit('cancel')
            },
            paginationChange(page, size) {
                this.size = size
                this.page = page
                this.fetchAllSMSMessages()
            },
            fetchAllSMSMessages() {
                this.isFetchingSms = true
                return smsCampaignService
                    .getSMS(this.getCurrentSMSCampaign.id, this.filter)
                    .then((resp) => {
                        this.smsMessages = resp.data.content.map((message, index) => ({
                            ...message,
                            index: index + (this.page - 1) * this.size + 1,
                            details: this.getMessageDetails(message)
                        }))
                        this.totalPages = resp.data.totalPages
                        this.totalItems = resp.data.totalElements
                    })
                    .finally(() => {
                        this.isFetchingSms = false
                    })
            },
            exportCSV() {
                this.disableExportButton = true
                smsCampaignService
                    .getSMSCampaignReport(this.getCurrentSMSCampaign.id)
                    .then((resp) => {
                        this.exportCsvFile(this.getCurrentSMSCampaign.alias, resp.data)
                    })
                    .catch((error) => {
                        this.setAlert(error.response.data.message, 'error')
                        this.disableExportButton = false
                    })
            },
            downloadFile() {
                smsCampaignService
                    .downloadUploadedFile(this.getCurrentSMSCampaign.id)
                    .then((resp) => {
                        this.exportCsvFile(
                            this.getCurrentSMSCampaign.alias + '_file_used',
                            resp.data
                        )
                    })
                    .catch((error) => {
                        this.setAlert(error.response.data.message, 'error')
                    })
            },
            exportCsvFile(fileName, fileData) {
                const data = 'text;charset=utf-8,' + encodeURIComponent(fileData)
                const link = document.createElement('a')
                link.href = 'data:' + data
                link.download = `${fileName}.csv`

                const container = document.getElementById('export')
                container.appendChild(link)
                link.click()
                this.disableExportButton = false
            },
            updateFilter(newFilter) {
                this.smsFilter = newFilter
                this.fetchAllSMSMessages()
            },
            getMessageDetails(message) {
                return tableUtils.getMessageDetails(message)
            },
            deleteSMSCampaign() {
                smsCampaignService.deleteSMSCampaign(this.getCurrentSMSCampaign.id).then(() => {
                    this.setAlert('SMS Campaign Successfully Deleted!', 'success')
                    setTimeout(() => {
                        this.$router.push('/sms')
                    }, 1000)
                }).catch(error => {
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            setAlert(message, type) {
                this.showAlert = true
                this.alertMessage = message
                this.alertType = type
            }
        }
    }
</script>

<style scoped>
.overview-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.info-container {
    display: flex;
    column-gap: 10px;
}

.table-wrapper {
    padding-bottom: 1rem;
    width: 100%;
}

.table-container {
    width: 100%;
}

.card-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
}

.charts-container {
    width: 100%;
}

.chart-row {
    width: 100%;
    display: flex;
    padding: 20px;
}

.export-btn {
    text-transform: none !important;
    background: #00a65a !important;
    color: white;
    margin: 15px auto;
    width: 150px;
}

.delete-dialog {
    display: flex;
    justify-content: flex-end;
    padding: 0 1rem;
}

@media screen and (max-width: 1370px) {
    .info-container {
        row-gap: 10px;
        flex-wrap: wrap;
    }

    .charts-container {
        justify-content: center;
        align-items: center;
        flex-flow: column wrap;
    }
}

@media (min-width: 993px) {
    .charts-container {
        padding: 2rem 1rem;
    }

    .export-btn {
        width: auto;
    }
}
</style>
